import React from 'react'
import {
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material'
import {
  TableColumn,
  SelectionConfig,
  SortingConfig,
  PrioritizationConfig,
  ExpandableConfig,
  TableData,
  ExpansionTriggerMode
} from '../types'

interface TableHeaderProps<T extends TableData> {
  columns: TableColumn<T>[]
  selection?: SelectionConfig<T>
  sorting?: SortingConfig<T>
  prioritization?: PrioritizationConfig<T>
  expandable?: ExpandableConfig<T>
  hasActions: boolean
  data: T[]
  // Expandable state management
  allRowsExpanded?: boolean
  hasExpandableRows?: boolean
  onToggleAllRows?: () => void
}

export const TableHeader = <T extends TableData>({
  columns,
  selection,
  sorting,
  prioritization,
  expandable,
  hasActions,
  data,
  allRowsExpanded = false,
  hasExpandableRows = false,
  onToggleAllRows
}: TableHeaderProps<T>) => {
  const styles = useStyles()

  // Helper para determinar el modo de expansión
  const getEffectiveTriggerMode = (): ExpansionTriggerMode => {
    if (!expandable) return 'button-only'
    if (expandable.expansionTriggerMode) {
      return expandable.expansionTriggerMode
    }
    // Retrocompatibilidad: expandOnRowClick legacy
    if (expandable.expandOnRowClick) {
      return 'row-click'
    }
    return 'button-only'
  }

  // Helper para determinar si debe mostrarse la columna de expandir
  const shouldShowExpandColumn = (): boolean => {
    if (!expandable) return false
    const triggerMode = getEffectiveTriggerMode()
    // Solo mostrar columna en modos 'button-only' y 'both'
    return triggerMode === 'button-only' || triggerMode === 'both'
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selection?.onSelectAll) {
      selection.onSelectAll(event.target.checked)
    }
  }

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !sorting) return

    const newDirection =
      sorting.field === column.key && sorting.direction === 'asc'
        ? 'desc'
        : 'asc'

    sorting.onSortChange(column.key, newDirection)
  }

  const getSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null

    if (sorting?.field === column.key) {
      return sorting.direction === 'asc' ? (
        <ArrowUpward fontSize='small' />
      ) : (
        <ArrowDownward fontSize='small' />
      )
    }

    return <UnfoldMore fontSize='small' className={styles.sortIconInactive} />
  }

  const isAllSelected =
    selection && selection.selectedIds.length === data.length && data.length > 0
  const isIndeterminate =
    selection &&
    selection.selectedIds.length > 0 &&
    selection.selectedIds.length < data.length

  const handleToggleAllRows = () => {
    if (onToggleAllRows) {
      onToggleAllRows()
    }
  }

  const renderExpandAllButton = () => {
    if (!expandable || !hasExpandableRows) return null

    return (
      <IconButton
        onClick={handleToggleAllRows}
        size='small'
        className={styles.expandAllButton}
        title={
          allRowsExpanded
            ? 'Contraer todas las filas'
            : 'Expandir todas las filas'
        }
      >
        {allRowsExpanded ? (
          <ExpandLessIcon fontSize='small' />
        ) : (
          <ExpandMoreIcon fontSize='small' />
        )}
      </IconButton>
    )
  }

  return (
    <TableHead>
      <TableRow>
        {selection && (
          <TableCell align='center' style={{ width: '50px' }} className={styles.headerCell}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAll}
              disabled={selection.isSingleSelection}
              style={{ color: 'white' }}
            />
          </TableCell>
        )}

        {prioritization?.enabled && (
          <TableCell
            align='center'
            style={{ width: '80px', position: 'relative' }}
            className={styles.headerCell}
          >
            <div className={styles.headerContent}>Prioridad</div>
          </TableCell>
        )}

        {columns.map((column) => (
          <TableCell
            key={String(column.key)}
            align={column.align || 'center'}
            style={{
              width: column.width,
              ...column.headerStyle,
              position: 'relative'
            }}
            className={`${styles.headerCell} ${
              column.sortable ? styles.sortableHeader : ''
            }`}
            onClick={() => handleSort(column)}
          >
            {column.headerTooltip ? (
              <Tooltip title={column.headerTooltip} arrow placement='top'>
                <div className={styles.headerContent}>
                  {column.title}
                  {getSortIcon(column)}
                </div>
              </Tooltip>
            ) : (
              <div className={styles.headerContent}>
                {column.title}
                {getSortIcon(column)}
              </div>
            )}
          </TableCell>
        ))}

        {shouldShowExpandColumn() && (
          <TableCell
            align='center'
            style={{ width: '50px', position: 'relative' }}
            className={styles.headerCell}
          >
            <div className={styles.expandHeader}>
              {expandable!.headerText || 'Expandir'} {renderExpandAllButton()}
            </div>
          </TableCell>
        )}

        {hasActions && (
          <TableCell
            align='center'
            style={{ width: '120px', position: 'relative' }}
            className={styles.headerCell}
          >
            <div className={styles.headerContent}>Acciones</div>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  )
}

const useStyles = makeStyles((theme) => ({
  headerCell: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'normal',
    borderBottom: 'none !important',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    },
    '&:first-child': {
      borderTopLeftRadius: '12px'
    },
    '&:last-child': {
      borderTopRightRadius: '12px'
    }
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5)
  },
  sortIconInactive: {
    opacity: 0.3,
    '&:hover': {
      opacity: 0.7
    }
  },
  expandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  expandAllButton: {
    padding: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  }
}))
